<script setup lang="ts">
import { getUserWorkspace } from "@dopplexr/editor/lib/connectors/gitea";
import { TooltipRoot as Tooltip, TooltipTrigger } from "reka-ui";
import { twMerge } from "tailwind-merge";
import { computed, ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";
import DoppleLogoIcon from "@/assets/dopple-logo-icon.svg";
import DoppleLogoStacked from "@/assets/dopple-logo-stacked.svg";
import IconTreeCorner from "@/assets/icons/tree-corner.svg";
import IconTreeSplit from "@/assets/icons/tree-split.svg";
import TooltipContent from "@/components/ui/tooltip/TooltipContent.vue";
import { useTopBarStore } from "@/stores/topBar";
import { convertPermissions, type Permission } from "@/stores/workspaces";
import { navItems, type RouteItem } from "./sideNavItems";
import WorkspaceSelector from "./WorkspaceSelector.vue";

defineProps<{
	isCollapsed?: boolean;
}>();

const route = useRoute();
const owner = computed(() => route.params.orgName as string);
const workspace = computed(() => route.params.workspaceName as string);

const workspacePermissions = ref<Permission[]>([]);

watch(
	() => route.params.workspaceName,
	async (newWorkspaceName) => {
		if (!newWorkspaceName) {
			return;
		}

		const res = await getUserWorkspace({
			owner: route.params.orgName as string,
			workspace: newWorkspaceName as string,
		});

		if (res.isErr()) {
			throw res.error;
		}
		workspacePermissions.value = convertPermissions(res.value.permissions);
	},
	{ immediate: true },
);

const build = import.meta.env.VITE_APP_BUILD;

// Sheet nav menu state
const topBarStore = useTopBarStore();
// Returns the nav items that should be displayed for the current route
const filteredNavItems = computed(() => {
	const finalNavItems = [];
	for (const category of navItems) {
		if (!category.displayFn || category.displayFn(route)) {
			if (
				!workspacePermissions.value?.includes(
					(category.permissionRequired as Permission) ?? "read",
				)
			) {
				continue;
			}
			const filteredChildren = [];
			for (const childItem of category.children) {
				if (!childItem.displayFn || childItem.displayFn(route)) {
					if (
						!workspacePermissions.value?.includes(
							(childItem.permissionRequired as Permission) ?? "read",
						)
					) {
						continue;
					}
					const filteredSubRoutes = [];
					if (
						childItem.children &&
						(!childItem.displaySubRoutesFn ||
							childItem.displaySubRoutesFn(route))
					) {
						for (const subRoute of childItem.children) {
							if (
								!workspacePermissions.value?.includes(
									(subRoute.permissionRequired as Permission) ?? "read",
								)
							) {
								continue;
							}
							filteredSubRoutes.push({ ...subRoute });
						}
					}
					filteredChildren.push({ ...childItem, children: filteredSubRoutes });
				}
			}
			finalNavItems.push({ ...category, children: filteredChildren });
		}
	}
	return finalNavItems;
});

function itemPath(path: string) {
	return path.startsWith("http")
		? path
		: path.startsWith("/")
			? path
			: `/owner/${owner.value}/w/${workspace.value}/${path}`;
}

function hasChildren(item: RouteItem): boolean {
	if (!item.children) {
		return false;
	}
	return "children" in item && item.children.length > 0;
}
</script>

<template>
	<RouterLink
		to="/"
		@click="topBarStore.isSheetMenuOpen = false"
		class="self-center my-4"
	>
		<component
			:is="DoppleLogoStacked"
			v-show="!isCollapsed"
			class="max-w-24 mx-auto h-auto text-brand-500 dark:text-foreground"
		/>
		<component
			:is="DoppleLogoIcon"
			v-show="isCollapsed"
			class="w-6 mx-auto h-auto text-brand-500 dark:text-foreground"
		/>
	</RouterLink>
	<slot name="collapse"></slot>
	<nav
		:class="
			twMerge('overflow-y-auto pb-4', $props.isCollapsed ? 'px-3' : 'px-4')
		"
	>
		<WorkspaceSelector v-if="!isCollapsed" />
		<div v-for="category in filteredNavItems" :key="category.title">
			<h2
				v-if="category.title !== '' && !isCollapsed"
				class="mt-4 px-2 py-2 text-xs text-slate-500 dark:text-slate-400 font-bold uppercase"
			>
				{{ category.title }}
			</h2>
			<hr
				v-show="category.title && isCollapsed"
				class="bg-slate-200 dark:bg-slate-700 w-8 h-1 mx-auto my-4 rounded-lg"
			/>
			<template v-for="(item, itemIndex) in category.children" :key="itemIndex">
				<Tooltip>
					<TooltipTrigger as-child v-show="$props.isCollapsed">
						<component
							:is="item.path.startsWith('http') ? 'a' : RouterLink"
							:href="itemPath(item.path)"
							:to="itemPath(item.path)"
							:target="item.path.startsWith('http') ? '_blank' : undefined"
							v-show="$props.isCollapsed"
							class="inline-flex items-center justify-start gap-2 h-10 px-2 w-full transition-colors relative rounded-lg bg-blue-500/0 hover:text-blue-500 after:-left-3"
							@click="topBarStore.isSheetMenuOpen = false"
						>
							<component :is="item.icon" class="size-6 shrink-0" />
						</component>
					</TooltipTrigger>
					<TooltipContent side="right" :side-offset="5">
						{{ item.title }}
					</TooltipContent>
					<component
						:is="item.path.startsWith('http') ? 'a' : RouterLink"
						:href="itemPath(item.path)"
						:to="itemPath(item.path)"
						:target="item.path.startsWith('http') ? '_blank' : undefined"
						v-show="!$props.isCollapsed"
						:class="
							twMerge(
								'inline-flex items-center justify-start gap-2 h-10 px-2 w-full transition-colors relative rounded-lg bg-blue-500/0 dark:bg-blue-300/0 hover:text-blue-500 dark:hover:text-blue-300 select-none after:-left-4',
								hasChildren(item) && 'has-subroutes',
							)
						"
						@click="topBarStore.isSheetMenuOpen = false"
					>
						<component :is="item.icon" class="size-6 shrink-0" />
						<span class="truncate">{{ item.title }}</span>
					</component>
				</Tooltip>
				<ul
					v-if="item.children && item.children.length && !$props.isCollapsed"
					class="mb-4"
				>
					<li
						v-for="(subroute, subrouteIndex) in item.children"
						:key="subrouteIndex"
						class="h-8"
					>
						<RouterLink
							:to="itemPath(subroute.path)"
							:class="
								twMerge(
									'inline-flex items-center justify-start gap-2 px-2 w-full transition-colors relative rounded-lg bg-blue-500/0 dark:bg-blue-300/0 hover:text-blue-500 dark:hover:text-blue-300 select-none after:-left-4',
									subroute.title === 'Workspace Overview' &&
										'sidenav-workspace-name',
								)
							"
							@click="topBarStore.isSheetMenuOpen = false"
						>
							<component
								:is="
									subrouteIndex === item.children.length - 1
										? IconTreeCorner
										: IconTreeSplit
								"
								class="w-6 h-8 shrink-0 text-slate-300"
							/>
							<span class="truncate">
								{{
									subroute.title === "Workspace Overview"
										? $route.params.workspaceName
										: subroute.title
								}}
							</span>
						</RouterLink>
					</li>
				</ul>
			</template>
		</div>
	</nav>
	<div v-if="build" class="build-number pb-2 pt-4 text-xs text-center hidden">
		Build Number: {{ build }}
	</div>
</template>

<style scoped>
@reference "@/style.css";

nav a::after {
	@apply content-[''] absolute top-0 h-full rounded-r-lg w-1 transition duration-500 -translate-x-[calc(100%+1px)];
}

nav a {
	/* There is a bug, `dark` doesn't work with `::after` */
	@apply after:bg-blue-500 dark:after:bg-blue-300;
}

nav a.router-link-exact-active,
nav a.router-link-active:not(:is(.has-subroutes, .sidenav-workspace-name)) {
	@apply text-blue-500 dark:text-blue-300 bg-blue-500/5 dark:bg-blue-300/5;
}

nav a.router-link-exact-active::after,
nav
	a.router-link-active:not(
		:is(.has-subroutes, .sidenav-workspace-name)
	)::after {
	@apply translate-x-0;
}
</style>
